// File converter — upload images / docs / video, convert to a chosen format.
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../shared/api/client";
import { PageHeader } from "../../shared/components/PageHeader";
import { toast } from "../../shared/stores/toasts";

type Cat = "image" | "pdf" | "office" | "video" | "audio";
type Formats = { categories: Record<string, string[]>; targets: Record<string, string[]> };
type Status = "idle" | "converting" | "done" | "error";
type Row = { id: string; file: File; cat: Cat | null; target: string; status: Status; msg?: string };

const CAT_LABEL: Record<string, string> = { image: "이미지", pdf: "PDF", office: "문서", video: "영상", audio: "오디오" };
const CAT_ICON: Record<string, string> = { image: "🖼️", pdf: "📄", office: "📝", video: "🎬", audio: "🎵" };

function ext(name: string): string { return name.split(".").pop()?.toLowerCase() ?? ""; }
function fmtSize(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
function decodeFilename(cd: string): string | null {
  const m = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (m) { try { return decodeURIComponent(m[1]); } catch { return m[1]; } }
  const m2 = /filename="?([^";]+)"?/i.exec(cd);
  return m2 ? m2[1] : null;
}
async function readBlobError(e: any): Promise<string | null> {
  const b = e?.response?.data;
  if (b instanceof Blob) {
    try { const t = await b.text(); try { return JSON.parse(t).detail; } catch { return t; } } catch { /* ignore */ }
  }
  return e?.response?.data?.detail ?? null;
}

export default function ConverterPage() {
  const [formats, setFormats] = useState<Formats | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.get<Formats>("/convert/formats").then(r => setFormats(r.data))
      .catch(() => toast.error("변환 형식 목록을 불러오지 못했습니다"));
  }, []);

  const catFor = (name: string): Cat | null => {
    if (!formats) return null;
    const e = ext(name);
    for (const [cat, exts] of Object.entries(formats.categories)) if (exts.includes(e)) return cat as Cat;
    return null;
  };

  const addFiles = (files: FileList | File[]) => {
    if (!formats) return;
    const next: Row[] = Array.from(files).map(file => {
      const cat = catFor(file.name);
      const targets = cat ? formats.targets[cat] : [];
      const srcExt = ext(file.name);
      const target = targets.find(t => t !== srcExt) ?? targets[0] ?? "";
      return { id: Math.random().toString(36).slice(2), file, cat, target, status: "idle" as Status };
    });
    setRows(prev => [...prev, ...next]);
  };

  const setRow = (id: string, patch: Partial<Row>) => setRows(rs => rs.map(r => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id: string) => setRows(rs => rs.filter(r => r.id !== id));

  const convertOne = async (row: Row) => {
    if (!row.cat) { setRow(row.id, { status: "error", msg: "지원하지 않는 형식" }); return; }
    if (!row.target) { setRow(row.id, { status: "error", msg: "변환 형식을 선택하세요" }); return; }
    setRow(row.id, { status: "converting", msg: undefined });
    try {
      const fd = new FormData();
      fd.append("file", row.file);
      fd.append("target", row.target);
      const res = await api.post("/convert", fd, { responseType: "blob" });
      const blob = res.data as Blob;
      const cd = (res.headers as any)["content-disposition"] ?? "";
      const fname = decodeFilename(cd) ?? `${row.file.name.replace(/\.[^.]+$/, "")}.${row.target}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setRow(row.id, { status: "done" });
    } catch (e: any) {
      const detail = (await readBlobError(e)) ?? "변환 실패";
      setRow(row.id, { status: "error", msg: detail });
      toast.error("변환 실패", detail);
    }
  };

  const convertAll = async () => {
    for (const r of rows) if (r.status !== "done" && r.status !== "converting") await convertOne(r);
  };

  const pending = useMemo(() => rows.filter(r => r.status === "idle" || r.status === "error").length, [rows]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <PageHeader title="파일 변환기" subtitle="이미지 · PDF · 문서 · 영상 · 오디오를 원하는 형식으로 변환" />

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 24px" }}>
        {/* 좌우 마진: 넓은 화면에서 콘텐츠가 퍼지지 않게 중앙 고정 폭 */}
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
        {/* Drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? "var(--p-500,#6366f1)" : "var(--border,#d4d4d8)"}`,
            background: dragOver ? "var(--p-50,rgba(99,102,241,0.06))" : "var(--bg-1,rgba(0,0,0,0.015))",
            borderRadius: 14, padding: "40px 20px", textAlign: "center", cursor: "pointer", transition: "all .15s",
          }}
        >
          <div style={{ fontSize: 44, lineHeight: 1, marginBottom: 10 }}>📂</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-1)" }}>파일을 끌어다 놓거나 클릭해서 선택</div>
          <div style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 6 }}>
            이미지(svg 포함) · PDF · 문서(docx·pptx·xlsx·hwp) · 영상 · 오디오 · 여러 개 동시 가능 (최대 500MB)
          </div>
          <input ref={inputRef} type="file" multiple hidden
            onChange={e => { if (e.target.files) addFiles(e.target.files); e.target.value = ""; }} />
        </div>

        {/* Batch bar */}
        {rows.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 2px 10px" }}>
            <div style={{ fontSize: 13, color: "var(--text-2)", fontWeight: 600 }}>{rows.length}개 파일</div>
            <div style={{ flex: 1 }} />
            <button className="t-btn" onClick={() => setRows([])}>전체 비우기</button>
            <button className="t-btn primary" disabled={!pending} onClick={convertAll}>
              ⚡ 전체 변환 {pending ? `(${pending})` : ""}
            </button>
          </div>
        )}

        {/* Rows */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rows.map(row => {
            const targets = row.cat && formats ? formats.targets[row.cat].filter(t => t !== ext(row.file.name)) : [];
            return (
              <div key={row.id} style={{
                display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                border: "1px solid var(--border)", borderRadius: 10, background: "var(--bg-0,#fff)",
              }}>
                <div style={{ fontSize: 26, lineHeight: 1 }}>{row.cat ? CAT_ICON[row.cat] : "❓"}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.file.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>
                    {row.cat ? CAT_LABEL[row.cat] : "지원 안 함"} · {fmtSize(row.file.size)}
                    {row.status === "error" && row.msg && <span style={{ color: "var(--danger,#ef4444)" }}> · {row.msg}</span>}
                    {row.status === "done" && <span style={{ color: "var(--success,#22c55e)" }}> · 완료 ✓</span>}
                  </div>
                </div>

                {row.cat ? (
                  <>
                    <span style={{ fontSize: 12, color: "var(--text-3)" }}>→</span>
                    <select className="t-input" value={row.target} disabled={row.status === "converting"}
                      onChange={e => setRow(row.id, { target: e.target.value, status: "idle", msg: undefined })}
                      style={{ width: 110, padding: "5px 8px", fontSize: 12.5 }}>
                      {targets.map(t => <option key={t} value={t}>.{t}</option>)}
                    </select>
                    <button className="t-btn primary" disabled={row.status === "converting"}
                      onClick={() => convertOne(row)} style={{ minWidth: 78 }}>
                      {row.status === "converting" ? "변환 중…" : row.status === "done" ? "다시" : "변환"}
                    </button>
                  </>
                ) : (
                  <span style={{ fontSize: 12, color: "var(--danger,#ef4444)" }}>지원하지 않는 형식</span>
                )}
                <button className="t-btn" onClick={() => removeRow(row.id)} title="제거" style={{ padding: "5px 9px" }}>✕</button>
              </div>
            );
          })}
        </div>

        {rows.length === 0 && (
          <div style={{ marginTop: 22, fontSize: 12.5, color: "var(--text-3)", lineHeight: 1.9 }}>
            <b style={{ color: "var(--text-2)" }}>지원 변환</b><br />
            🖼️ 이미지(svg 포함) → png · jpg · webp · gif · bmp · tiff · ico · pdf<br />
            📄 PDF → png · jpg · webp · docx · txt (여러 페이지는 zip)<br />
            📝 문서(docx·pptx·xlsx·hwp·odt) → pdf · docx · xlsx · pptx · txt · html<br />
            🎬 영상 → mp4 · webm · mkv · mov · avi · gif · mp3 · wav<br />
            🎵 오디오 → mp3 · wav · ogg · flac · aac · m4a
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
